/**
 * Where a lazily loaded page renders: a Suspense boundary with a quiet loading
 * state, inside an error boundary. A chunk that fails to load (offline, or the
 * app was redeployed and the old file is gone) or a page that throws while
 * rendering shows a notice with a reload button instead of blanking the shell.
 * The error resets when the path changes, so the navigation keeps working.
 */

import { Component, Suspense, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { LoadingBlock, Notice } from '../components/ui';

/** Below this, a chunk usually arrives before anyone would notice a placeholder. */
const LOADING_DELAY_MS = 200;

function PageLoading(): JSX.Element {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVisible(true);
    }, LOADING_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);
  return visible ? <LoadingBlock label="正在加载页面…" /> : <div aria-busy="true" />;
}

/** Messages browsers use when a dynamic import or its preload fails. */
const CHUNK_ERROR =
  /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError/i;

function isChunkError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(`${error.name} ${error.message}`);
}

interface BoundaryProps {
  readonly resetKey: string;
  readonly children: ReactNode;
}

interface BoundaryState {
  readonly error: unknown;
  readonly failedKey: string | null;
}

class RouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null, failedKey: null };

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: BoundaryProps,
    state: BoundaryState,
  ): Partial<BoundaryState> | null {
    if (state.error === null) return null;
    // First render after the failure: remember where it happened.
    if (state.failedKey === null) return { failedKey: props.resetKey };
    // Navigated elsewhere: render the new page.
    if (state.failedKey !== props.resetKey) return { error: null, failedKey: null };
    return null;
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('页面渲染失败', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    const chunk = isChunkError(error);
    return (
      <Notice
        tone="error"
        title={chunk ? '页面加载失败' : '页面出错了'}
        detail={
          chunk
            ? '页面文件没有下载成功，可能是网络中断，或工作台刚刚更新过版本。重新加载即可获取最新版本。'
            : '这个页面在显示时遇到了意外错误。可以重新加载页面，或先切换到其他页面。'
        }
        actions={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              window.location.reload();
            }}
          >
            重新加载页面
          </button>
        }
      />
    );
  }
}

/** Wraps one lazily loaded element (a top-level route or a layout). */
export function RouteBoundary({ children }: { readonly children: ReactNode }): JSX.Element {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<PageLoading />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

/** A layout's `<Outlet />` behind the same boundary, so the shell stays put. */
export function RouteOutlet(): JSX.Element {
  return (
    <RouteBoundary>
      <Outlet />
    </RouteBoundary>
  );
}
